import { FC, useEffect, ReactElement } from 'react';

import { observer } from 'mobx-react';
import { Navigate, useLocation } from 'react-router-dom';

import { useStores } from 'mobx/hooks/useStores';

import AuditService from 'fetchers/AuditFetcher';

import Layout from 'containers/Layout/Layout';

import { TAB_QUERY_PARAM } from 'views/Patient/PatientMain/PatientMainView.constants';

function addPageViewAudit(pathname: string, search: string) {
  const searchParams = new URLSearchParams(search);
  const tabName = searchParams.get(TAB_QUERY_PARAM); // if not exists - tabName will be null
  const queryParams: Record<string, string> = {};

  // currently we have no standard way to include tabs in the URL, so only patient view tabs treated here
  // see https://expain.atlassian.net/browse/EH-2447
  searchParams.forEach((value: string, key: string) => {
    if (key !== TAB_QUERY_PARAM) {
      queryParams[key] = value;
    }
  });

  AuditService.addPageViewAudit({
    pageName: pathname,
    tabName,
    queryParams
  });
}

interface Props {
  shouldAllow?: boolean;
  redirectRoute?: string;
  element: ReactElement;
}

const PrivateRoute: FC<Props> = observer(({ element, redirectRoute, shouldAllow = true }) => {
  const location = useLocation();
  const { userStore } = useStores();

  useEffect(
    function logRouteChanges() {
      // Log page view when route changes
      if (userStore.isAuthenticated) {
        const isPatientPageIndexRoute = location.pathname.includes('/patient/') && !location.search;
        // skip route "/patient/123" since it temporary (redirected to default tab)
        if (!isPatientPageIndexRoute) {
          addPageViewAudit(location.pathname, location.search);
        }
      }
    },
    [location.pathname, location.search, userStore.isAuthenticated]
  );

  const isAllowed = () => {
    if (!userStore.isAuthenticated) {
      return false;
    }

    return shouldAllow;
  };

  if (isAllowed()) {
    return <Layout>{element}</Layout>;
  }

  return <Navigate to={redirectRoute || '/'} state={{ from: location }} replace />;
});

export default PrivateRoute;
